// Shared converter between Content Studio Quiz format and QuizBuilder (Pathway) format
// Used by both QuizViewer (Content Studio) and PathwayManagement (quiz module import)

import type { Quiz, QuizQuestion } from './types';
import type { QuizConfig as BuilderQuizConfig, QuizQuestion as BuilderQuestion } from '@/components/pathways/QuizBuilder';

/**
 * Convert a Content Studio Quiz → QuizBuilder config (for editing in PathwayManagement)
 *
 * Content Studio uses: type "multiple-choice"/"true-false"/"scenario", correctAnswer as index/boolean
 * QuizBuilder uses: question_type "multiple_choice"/"true_false", correct_answer as index number
 */
export function contentStudioToBuilder(quiz: Quiz): BuilderQuizConfig {
  return {
    quiz_type: 'mixed',
    passing_score: 70,
    questions: quiz.questions.map((q) => {
      let questionType: BuilderQuestion['question_type'] = 'multiple_choice';
      if (q.type === 'true-false') questionType = 'true_false';

      let options = q.options || [];
      if (q.type === 'true-false') options = ['True', 'False'];

      let correctAnswer: number | number[] = 0;
      if (q.type === 'multiple-choice' || q.type === 'scenario') {
        correctAnswer = Number(q.correctAnswer);
      } else if (q.type === 'true-false') {
        correctAnswer = q.correctAnswer === true ? 0 : 1;
      }

      return {
        id: q.id,
        question: q.question,
        options,
        correct_answer: correctAnswer,
        explanation: q.explanation,
        points: 1,
        question_type: questionType,
      };
    }),
    show_explanations: true,
    shuffle_questions: false,
    shuffle_options: false,
  };
}

/**
 * Convert a QuizBuilder config → Content Studio Quiz (for grading via Edge Function)
 */
export function builderToContentStudio(config: BuilderQuizConfig, original: Quiz): Quiz {
  return {
    ...original,
    totalQuestions: config.questions.length,
    questions: config.questions.map((q) => {
      let type: QuizQuestion['type'] = 'multiple-choice';
      if (q.question_type === 'true_false') type = 'true-false';

      let correctAnswer: QuizQuestion['correctAnswer'] = q.correct_answer as number;
      if (type === 'true-false') {
        correctAnswer = (q.correct_answer as number) === 0;
      }

      return {
        id: q.id,
        type,
        difficulty: 'medium' as const,
        topic: '',
        question: q.question,
        options: q.options,
        correctAnswer,
        explanation: q.explanation || '',
      };
    }),
  };
}
